import { PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  useInvestigationsServiceGetInvestigationTestsEndpoint,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpoint,
  useInvestigationsServiceGetListSpecimensEndpoint,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useTreatmentServiceGetListNewbornInvestigationEndpoint,
  useTreatmentServiceGetListPatientNewbornsEndpoint,
  useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint
} from 'src/api/queries'
import {
  Countries,
  IdentifierWithTranslationDto,
  InvestigationTestResultDto,
  Permissions,
  SpecimenItemDto
} from 'src/api/requests'
import { Paragraph, SearchBar, TbTag, Text } from 'src/components'
import {
  isExternalResult,
  ResultWithApproval
} from 'src/features/persons/utils'
import { TestStatusTag } from 'src/features/tests/components'
import { getFullName } from 'src/features/users/utils'
import { useTablePagination, useUser } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  formatUtcDateTime
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import {
  AssignInvestigationModal,
  ExternalResultModal,
  InvestigationDemandModalScreening,
  PersonInvestigationsFilters,
  ResultActions
} from '../components'
import { InvestigationDemandModal } from '../components/PersonConsultations/InvestigationDemandModal'
import { usePersonBreadcrumb } from '../hooks'
import { useStyles } from '../styles'

type Props = {
  showPregnantOnly?: boolean
}

export const PersonInvestigationsPage = ({ showPregnantOnly }: Props) => {
  const { styles } = useStyles()
  const { t, currentLang } = useLocales()
  const { personId, id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const personRouteId = String(personId ?? id)
  const isPatientContext = location.pathname.includes('/patients/')

  const pagination = useTablePagination()

  const [investigationId, setInvestigationId] = useState<string>()
  const [isOpenFilters, setIsOpenFilters] = useState(false)
  const { hasPermission } = useUser()

  const { person } = usePersonBreadcrumb(personId ?? id)
  const [isOpen, setIsOpen] = useState(false)
  const [isTbOpen, setIsTbOpen] = useState(false)
  const [isExternalOpen, setIsExternalOpen] = useState(false)
  const [tbFilter, setTbFilter] = useState<'all' | 'tb' | 'nonTb'>('all')

  const onToggle = () => setIsOpen((prev) => !prev)
  const onToggleTb = () => setIsTbOpen((prev) => !prev)
  const onToggleExternal = () => setIsExternalOpen((prev) => !prev)
  const queryClient = useQueryClient()

  const onSuccessCreate = () => {
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
    })
  }
  const queryParamsNewBorns = {
    pageSize: 100,
    personIds: [String(personId)],
    pregnancyMonitoringIds: [String(id)]
  }

  const { data: newBorns } = useTreatmentServiceGetListPatientNewbornsEndpoint(
    {
      ...queryParamsNewBorns
    },
    [],
    {
      enabled: !!showPregnantOnly
    }
  )

  const isForeignPerson =
    !!person?.citizenship && person.citizenship !== Countries.MD

  const canAddInvestigation =
    isForeignPerson &&
    hasPermission(Permissions.INVESTIGATIONS_INVESTIGATIONS_CREATE_FOREIGN)

  const isScreening = !hasPermission([
    Permissions.PERSONS_PATIENT_VIEW_OWN,
    Permissions.PERSONS_PATIENT_VIEW_INSTITUTION
  ])

  const { newBornsIds, newBornsPersonIds } = useMemo(() => {
    const ids: string[] = []
    const personIds: string[] = []

    if (newBorns?.data) {
      for (const item of newBorns.data) {
        if (item?.id != null) ids.push(item.id)
        if (item?.personId != null) personIds.push(item.personId)
      }
    }

    return { newBornsIds: ids, newBornsPersonIds: personIds }
  }, [newBorns?.data])

  const { data: pregnancyMonitoringInvestigations } =
    useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint(
      {
        pageNumber: 1,
        pageSize: 100,
        ids: [String(id)]
      },
      [String(id)],
      { enabled: !!showPregnantOnly }
    )

  const { data: newbornInvestigations } =
    useTreatmentServiceGetListNewbornInvestigationEndpoint(
      {
        pageNumber: 1,
        pageSize: 100,
        ids: newBornsIds,
        pregnancyMonitoringIds: [String(id)]
      },
      [newBornsIds, String(id)],
      { enabled: !!showPregnantOnly && newBornsIds.length > 0 }
    )

  const [filters, setFilters] = useState<
    Record<string, string | undefined> | undefined | { personIds: string[] }
  >(() => {
    if (showPregnantOnly) {
      return {
        isPregnancyMonitoring: true,
        isNewborn: true,
        personIds: [String(personId)]
      }
    }
    return undefined
  })

  useEffect(() => {
    if (showPregnantOnly && newBornsPersonIds.length > 0) {
      setFilters((prev) => ({
        ...prev,
        personIds: [String(personId), ...newBornsPersonIds]
      }))
    }
  }, [showPregnantOnly, newBornsPersonIds, personId])

  const demandIds = showPregnantOnly
    ? [
        ...(pregnancyMonitoringInvestigations?.data
          ? pregnancyMonitoringInvestigations.data
              .map((item) => item?.investigationDemand?.id)
              .filter((id): id is string => Boolean(id))
          : []),

        ...(newbornInvestigations?.data
          ? newbornInvestigations.data
              .map((item) => item?.investigationDemand?.id)
              .filter((id): id is string => Boolean(id))
          : [])
      ]
    : []

  const queryParams = useMemo(() => {
    // No institutionIds filter: investigations are created under the selected
    // lab's institution, so scoping to the person's institution would hide them.
    // The query is person-scoped (personIds) like the screening page.
    const baseParams = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }

    let personIds: string[] = []

    if (filters?.personIds && filters.personIds.length > 0) {
      personIds = Array.isArray(filters.personIds)
        ? filters.personIds
        : [filters.personIds]
    } else if (person?.id) {
      personIds = [String(person.id)]
    }

    return {
      ...baseParams,
      ...filters,
      personIds,
      demandIds
    }
  }, [pagination, filters, person, demandIds])

  const shouldFetch =
    !showPregnantOnly || (showPregnantOnly && demandIds.length > 0)

  const { data, isLoading } = useInvestigationsServiceGetListSpecimensEndpoint(
    queryParams,
    [JSON.stringify(queryParams)],
    { enabled: shouldFetch }
  )

  const pageSpecimenIds = useMemo(
    () =>
      (data?.data ?? [])
        .map((specimen) => specimen.id)
        .filter((id): id is string => !!id),
    [data?.data]
  )

  // The specimens list doesn't carry the investigation type, so we fetch the
  // investigations for the specimens on this page and group their types back
  // onto each specimen row by specimen code.
  const { data: specimenInvestigations } =
    useInvestigationsServiceGetListSpecimenInvestigationsEndpoint(
      { institutionIds: [], specimenIds: pageSpecimenIds, pageSize: 100 },
      [JSON.stringify(pageSpecimenIds)],
      { enabled: pageSpecimenIds.length > 0 }
    )

  const investigationTypesByCode = useMemo(() => {
    const map = new Map<string, IdentifierWithTranslationDto[]>()

    for (const investigation of specimenInvestigations?.data ?? []) {
      if (!investigation.specimenCode || !investigation.type) continue

      const types = map.get(investigation.specimenCode) ?? []

      if (!types.some((type) => type.id === investigation.type?.id)) {
        types.push(investigation.type)
      }

      map.set(investigation.specimenCode, types)
    }

    return map
  }, [specimenInvestigations?.data])

  // Results live on investigation tests, not on the specimen list. Fetch the
  // tests for the specimens on this page and group their results back onto each
  // specimen row via investigationId -> specimenCode (from specimenInvestigations).
  const { data: pageTests } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      { specimenIds: pageSpecimenIds, pageSize: 100 },
      [JSON.stringify(pageSpecimenIds)],
      { enabled: pageSpecimenIds.length > 0 }
    )

  const resultsBySpecimenCode = useMemo(() => {
    const specimenCodeByInvestigationId = new Map<string, string>()
    const typeByInvestigationId = new Map<
      string,
      IdentifierWithTranslationDto
    >()
    for (const investigation of specimenInvestigations?.data ?? []) {
      if (investigation.id && investigation.specimenCode) {
        specimenCodeByInvestigationId.set(
          investigation.id,
          investigation.specimenCode
        )
      }
      if (investigation.id && investigation.type) {
        typeByInvestigationId.set(investigation.id, investigation.type)
      }
    }

    const map = new Map<string, ResultWithApproval[]>()
    for (const test of pageTests?.data ?? []) {
      const specimenCode = test.investigationId
        ? specimenCodeByInvestigationId.get(test.investigationId)
        : undefined

      if (!specimenCode || !test.results?.length) continue

      const results = map.get(specimenCode) ?? []
      results.push(
        ...test.results.map((result) => ({
          result,
          isApproved: test.isApproved,
          referralDoctorUser: test.referralDoctorUser,
          referralDoctorName: test.referralDoctorName,
          typeId: test.investigationId
            ? typeByInvestigationId.get(test.investigationId)?.id
            : undefined
        }))
      )
      map.set(specimenCode, results)
    }

    return map
  }, [pageTests?.data, specimenInvestigations?.data])

  const onToggleFilters = () => setIsOpenFilters((prev) => !prev)

  const onSearch = (val: string) => {
    setFilters((prev) => ({ ...prev, searchTerm: val ? val : undefined }))
    onFiltersChange()
  }

  const onFiltersChange = () => {
    resetPagination(pagination)
  }

  const onCloseModal = () => setInvestigationId(undefined)

  const goToResults = (specimenId: string) => {
    const detailsBase = isPatientContext
      ? paths.patients.details(personRouteId)
      : paths.persons.details(personRouteId)
    const resultsRoot = isPatientContext
      ? paths.patients.investigationResults.root()
      : paths.persons.investigationResults.root()

    navigate(
      `${detailsBase}${resultsRoot}?specimenId=${specimenId}&from=investigations`
    )
  }

  const columns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'created date',
      title: t('investigations.performed_date'),
      render: ({ createdAt }) => formatUtcDateTime(createdAt)
    },
    {
      key: 'harvest date',
      title: t('investigations.harvest_date'),
      render: ({ harvestDate }) =>
        harvestDate
          ? dayjs
              .utc(harvestDate)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type }) => type?.translation?.[currentLang.value] ?? '-'
    },
    {
      key: 'investigation type',
      title: t('classifiers.category'),
      render: (_, { code }) => {
        const types = code ? (investigationTypesByCode.get(code) ?? []) : []

        if (!types.length) return '-'

        return (
          <div>
            {types.map((item) => (
              <div key={item.id}>{item.translation?.[currentLang.value]}</div>
            ))}
          </div>
        )
      }
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'test type',
      title: t('tests_results.type'),
      render: (_, { demand }) => {
        const text = (
          <div>
            {demand?.categories?.map((item) => (
              <div key={item.id}>{item.translation?.[currentLang.value]}</div>
            ))}
          </div>
        )

        return (
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: text
            }}
          >
            {text}
          </Paragraph>
        )
      }
    },
    {
      key: 'results',
      title: t('analysis.result'),
      render: (_, { code, id: specimenId }) => {
        const results = code ? (resultsBySpecimenCode.get(code) ?? []) : []

        if (!results.length) return '-'

        // Prefer the device/laboratory status; fall back to the manual result
        // option / value when it is empty.
        const formatValue = (result: InvestigationTestResultDto) =>
          result.laboratoryResultStatus ||
          [
            result.resultOption?.translation?.[currentLang.value],
            result.resultValueOption?.translation?.[currentLang.value],
            result.resultValue
          ]
            .filter(
              (value) => value !== null && value !== undefined && value !== ''
            )
            .join(', ') ||
          '-'

        return (
          <Flex vertical gap={2}>
            {results.map(
              (
                {
                  result,
                  isApproved,
                  referralDoctorUser,
                  referralDoctorName,
                  typeId
                },
                index
              ) => (
                <Flex key={index} gap={4} align="center" wrap="wrap">
                  <Text>
                    {result.testType?.translation?.[currentLang.value] ?? '-'}:{' '}
                    {formatValue(result)}
                  </Text>
                  {isExternalResult(result) && (
                    <Tag color="orange">
                      {t('persons.external_result.badge')}
                    </Tag>
                  )}
                  <ResultActions
                    result={result}
                    isApproved={isApproved}
                    specimenId={specimenId}
                    referralDoctorUserId={referralDoctorUser?.id}
                    referralDoctorName={referralDoctorName}
                    referralDoctorUserName={getFullName(
                      referralDoctorUser?.firstName,
                      referralDoctorUser?.lastName
                    )}
                    typeId={typeId}
                  />
                </Flex>
              )
            )}
          </Flex>
        )
      }
    },
    {
      key: 'test_results',
      title: t('investigations.tests'),
      render: (_, { id: specimenId }) => (
        <Button
          type="link"
          onClick={() => specimenId && goToResults(specimenId)}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  // The list endpoint has no isTb param, so the TB / non-TB toggle filters the
  // current page client-side. 'all' shows everything.
  const filteredSpecimens = (data?.data ?? []).filter((specimen) => {
    if (tbFilter === 'tb') return !!specimen.isTb
    if (tbFilter === 'nonTb') return !specimen.isTb

    return true
  })

  return (
    <Flex vertical gap={16}>
      <Flex justify={showPregnantOnly ? 'end' : 'space-between'} align="center">
        {!showPregnantOnly && (
          <Text className={styles.text}>
            {t('persons.person_investigations')}
          </Text>
        )}

        <Flex gap={8}>
          <SearchBar
            maxWidth={300}
            onSearch={onSearch}
            placeholder={t('investigations.search_placeholder')}
          />

          <Button icon={<SettingOutlined />} onClick={onToggleFilters}>
            {t('common.filters')}
          </Button>
          <Button type="primary" onClick={onToggle}>
            <PlusOutlined />
            {t('persons.investigations.investigation_new')}
          </Button>

          <Button type="primary" onClick={onToggleTb}>
            <PlusOutlined />
            {t('consultations.new_tb_investigation_request')}
          </Button>

          <Button type="primary" onClick={onToggleExternal}>
            <PlusOutlined />
            {t('persons.external_result.add')}
          </Button>
        </Flex>
      </Flex>

      {isOpenFilters && (
        <PersonInvestigationsFilters
          newBornsIds={newBornsIds}
          showPregnantOnly={showPregnantOnly}
          setFilters={setFilters}
          onChange={onFiltersChange}
          tbFilter={tbFilter}
          setTbFilter={setTbFilter}
        />
      )}

      {investigationId && (
        <AssignInvestigationModal
          id={investigationId}
          onCancel={onCloseModal}
        />
      )}

      <Table
        bordered
        dataSource={filteredSpecimens}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && (canAddInvestigation || isScreening) && (
        <InvestigationDemandModalScreening
          onSuccess={onSuccessCreate}
          onCancel={onToggle}
        />
      )}

      {isOpen && !canAddInvestigation && !isScreening && (
        <InvestigationDemandModal
          onSuccess={onSuccessCreate}
          onCancel={onToggle}
        />
      )}

      {isTbOpen && (
        <InvestigationDemandModal
          isTb
          onSuccess={onSuccessCreate}
          onCancel={onToggleTb}
        />
      )}

      {isExternalOpen && (
        <ExternalResultModal
          personId={personRouteId}
          onSuccess={onSuccessCreate}
          onCancel={onToggleExternal}
        />
      )}
    </Flex>
  )
}
